import { Subscription } from 'Core/Subscription'
import { secondsToHoursMinutesSeconds } from 'Utils/common.utils'

export enum TimerEventSubscriptionType {
    Elapsed = 'elapsed',
    Stopped = 'stopped',
    Paused = 'paused',
    Started = 'started',
}

export type OnElapsedParams = {
    hours?: number
    minutes: number
    seconds: number
}

export type TimerEventDataType = {
    [TimerEventSubscriptionType.Elapsed]: OnElapsedParams
    [TimerEventSubscriptionType.Stopped]: void
    [TimerEventSubscriptionType.Paused]: void
    [TimerEventSubscriptionType.Started]: void
}

export enum TimerType {
    CountUp = 'count-up',
    CountDown = 'count-down',
}

export type TimerStartParams =
    | {
          elapsedTimeInSec?: number
          type: TimerType.CountUp
      }
    | {
          type: TimerType.CountDown
          durationInSec: number
      }

export class Timer {
    private startTime: number
    private elapsedTime: number
    private isRunning: boolean
    private interval?: number

    constructor() {
        this.startTime = 0
        this.elapsedTime = 0
        this.isRunning = false
    }

    private subscription: Subscription<TimerEventSubscriptionType, TimerEventDataType> =
        new Subscription()

    onElapsed(cb: (elapsedTime: OnElapsedParams) => void): string {
        return this.subscription.subscribe(TimerEventSubscriptionType.Elapsed, cb)
    }

    private setStart = (elapsedTimeInSec?: number) => {
        if (elapsedTimeInSec) {
            this.elapsedTime = elapsedTimeInSec * 1000
        }
        this.startTime = Date.now() - this.elapsedTime
        this.isRunning = true
    }

    private startCountUp = (elapsedTimeInSec?: number) => {
        if (!this.isRunning) {
            this.setStart(elapsedTimeInSec)
            this.interval = window.setInterval(() => {
                this.publishElapsedCountUp()
            }, 1000)
            this.subscription.publishUpdate(TimerEventSubscriptionType.Started)
        }
    }

    publishElapsedCountUp() {
        const elapsed = Math.floor(this.getElapsedTime() / 1000)
        const minutes = Math.floor(elapsed / 60)
        const seconds = Math.floor(elapsed % 60)
        this.subscription.publishUpdate(TimerEventSubscriptionType.Elapsed, {
            minutes,
            seconds,
        })
    }

    private startCountDown = (durationInSec: number) => {
        if (!this.isRunning) {
            this.setStart()
            this.interval = window.setInterval(() => {
                const elapsed = Math.floor(this.getElapsedTime() / 1000)
                const timeLeftInSeconds = durationInSec - elapsed
                if (timeLeftInSeconds <= 0) {
                    this.stop()
                    return
                }
                const {
                    hours,
                    minutes,
                    remainingSeconds: seconds,
                } = secondsToHoursMinutesSeconds(timeLeftInSeconds)
                this.subscription.publishUpdate(TimerEventSubscriptionType.Elapsed, {
                    hours,
                    minutes,
                    seconds,
                })
            }, 1000)
            this.subscription.publishUpdate(TimerEventSubscriptionType.Started)
        }
    }

    start = (params: TimerStartParams) => {
        switch (params.type) {
            case TimerType.CountUp:
                this.startCountUp(params.elapsedTimeInSec)
                break
            case TimerType.CountDown:
                this.startCountDown(params.durationInSec)
                break
        }
    }

    stop() {
        this.elapsedTime = 0
        this.isRunning = false
        window.clearInterval(this.interval)
        this.subscription.publishUpdate(TimerEventSubscriptionType.Stopped)
    }

    pause() {
        if (this.isRunning) {
            this.elapsedTime = Date.now() - this.startTime
            this.publishElapsedCountUp()
            this.isRunning = false
            window.clearInterval(this.interval)
            this.subscription.publishUpdate(TimerEventSubscriptionType.Paused)
        }
    }

    getElapsedTime = () => {
        if (this.isRunning) {
            return Date.now() - this.startTime
        } else {
            return this.elapsedTime
        }
    }

    offElapsed(subscriptionId: string) {
        this.subscription.unsubscribe(subscriptionId)
    }

    onStopped(cb: () => void): string {
        return this.subscription.subscribe(TimerEventSubscriptionType.Stopped, cb)
    }

    offStopped(subscriptionId: string) {
        this.subscription.unsubscribe(subscriptionId)
    }
}
